import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { irisError } from '../errors';
import LoadingDots from './LoadingDots';
import ThemeToggle from './ThemeToggle';
import Dashboard from './Dashboard';
import Messenger from './Messenger';
import EntityProfileForm from './EntityProfileForm';
import Resources from './Resources';

const FONT = "'DM Sans', system-ui, sans-serif";

const TRADES = ['Roofing', 'Plumbing', 'HVAC', 'Other'];

const AEO_CATEGORIES = [
  { key: 'citability_score',       label: 'AI Citability',        weight: '25%', color: '#0A84FF' },
  { key: 'brand_authority_score',  label: 'Brand Authority',      weight: '20%', color: '#BF5AF2' },
  { key: 'content_eeat_score',     label: 'Content E-E-A-T',      weight: '20%', color: '#30D158' },
  { key: 'technical_geo_score',    label: 'Technical AEO',        weight: '15%', color: '#FF9F0A' },
  { key: 'schema_score',           label: 'Schema & Data',        weight: '10%', color: '#FF6482' },
  { key: 'platform_score',         label: 'Platform Optimization', weight: '10%', color: '#FFD60A' },
];

const SEVERITY_COLORS = { critical: '#FF453A', high: '#FF9F0A', medium: '#FFD60A', low: '#0A84FF' };

const AEO_PLATFORMS = ['Google AI Overviews', 'ChatGPT', 'Perplexity', 'Gemini', 'Bing Copilot'];

const PILLAR_OPTIONS = [
  'AI Citability',
  'Brand Authority',
  'Content E-E-A-T',
  'Technical AEO',
  'Schema & Data',
  'Platform Optimization',
  'General',
];

const AUDIT_API_URL = process.env.REACT_APP_AUDIT_API_URL || 'http://localhost:8080';

// ── Helpers ──────────────────────────────────────────────────────────────────

function auditsBucketPathFromPdfUrl(pdfUrl) {
  if (!pdfUrl || typeof pdfUrl !== 'string') return null;
  const marker = '/object/public/audits/';
  const i = pdfUrl.indexOf(marker);
  if (i === -1) return null;
  return decodeURIComponent(pdfUrl.slice(i + marker.length).split('?')[0]);
}

function parseAeoJson(data) {
  const scores = data.scores || {};
  return {
    geo_score: data.geo_score ?? data.overall_score ?? null,
    citability_score: scores.ai_citability ?? null,
    brand_authority_score: scores.brand_authority ?? null,
    content_eeat_score: scores.content_eeat ?? null,
    technical_geo_score: scores.technical ?? null,
    schema_score: scores.schema ?? null,
    platform_score: scores.platform_optimization ?? null,
    platforms: data.platforms ?? null,
    findings: data.findings ?? null,
    action_plan: data.action_plan ?? (data.quick_wins || data.medium_term || data.strategic ? {
      quick_wins: data.quick_wins || [],
      medium_term: data.medium_term || [],
      strategic: data.strategic || [],
    } : null),
    crawler_access: data.crawler_access ?? null,
    business_type: data.business_type ?? null,
    pages_analyzed: data.pages_analyzed ?? null,
    executive_summary: data.executive_summary ?? null,
  };
}

function countdownDays(audits, type, intervalDays) {
  const last = audits.find(a => a.audit_type === type);
  if (!last) return null;
  const lastDate = new Date(last.uploaded_at);
  const nextDate = new Date(lastDate.getTime() + intervalDays * 24 * 60 * 60 * 1000);
  const diff = Math.ceil((nextDate - Date.now()) / (1000 * 60 * 60 * 24));
  return Math.max(0, diff);
}

function parseAuditBreakdown(text) {
  const items = [];
  let currentPillar = 'General';
  for (const line of text.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const pillarMatch = trimmed.match(/^(?:Pillar\s+\d+:\s*)?(.+?)\s*[—–-]\s*\d+\/\d+/);
    if (pillarMatch) {
      currentPillar = pillarMatch[1].trim();
      continue;
    }
    if (/^[✓✔]/.test(trimmed)) {
      items.push({ description: trimmed.replace(/^[✓✔]\s*/, ''), status: 'done', pillar: currentPillar });
      continue;
    }
    if (/^[✗✘✕×]/.test(trimmed)) {
      items.push({ description: trimmed.replace(/^[✗✘✕×]\s*/, ''), status: 'todo', pillar: currentPillar });
      continue;
    }
  }
  return items;
}

const TAG_COLORS = [
  { value: '', label: 'None' },
  { value: '#e63030', label: 'Red' },
  { value: '#FF9F0A', label: 'Orange' },
  { value: '#FFD60A', label: 'Yellow' },
  { value: '#30D158', label: 'Green' },
  { value: '#0A84FF', label: 'Blue' },
  { value: '#BF5AF2', label: 'Purple' },
  { value: '#FF6482', label: 'Pink' },
];

function encodeTaskDesc(text, pillar, notes, color) {
  const obj = { d: text, p: pillar || 'General', n: notes || '' };
  if (color) obj.c = color;
  return JSON.stringify(obj);
}

function decodeTaskRow(row) {
  let desc = row.description || '';
  let pillar = 'General';
  let notes = null;
  let tagColor = '';
  try {
    if (desc.startsWith('{')) {
      const parsed = JSON.parse(desc);
      if (parsed._t) return null;
      desc = parsed.d || desc;
      pillar = parsed.p || 'General';
      notes = parsed.n || null;
      tagColor = parsed.c || '';
    }
  } catch (e) { /* plain text fallback */ }
  return { ...row, description: desc, pillar, notes, tagColor, status: row.completed_at ? 'done' : 'todo' };
}

function isAuditRequest(row) {
  try {
    const p = JSON.parse(row.description || '');
    return p._t === 'r';
  } catch { return false; }
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={S.label}>{label}</label>
      {children}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function CollapsibleSection({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginTop: 32 }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none',
        cursor: 'pointer', padding: '0 0 12px 0', fontFamily: FONT, width: '100%',
        borderBottom: '1px solid var(--border-subtle)',
      }}>
        <span style={{ fontSize: 10, color: 'var(--text-faint)', transition: 'transform 0.2s cubic-bezier(0.25,0.46,0.45,0.94)', transform: open ? 'rotate(90deg)' : 'rotate(0)', display: 'inline-block' }}>&#9654;</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', letterSpacing: '-0.01em', flex: 1, textAlign: 'left' }}>{title}</span>
        <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div style={{ marginTop: 20 }}>{children}</div>}
    </div>
  );
}

// ── New Client Form ──────────────────────────────────────────────────────────
function NewClientForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({ company_name: '', trade: 'Roofing', county: '', website_url: '' });
  const [authorizedEmails, setAuthorizedEmails] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(false);

  function addEmail(e) {
    e.preventDefault();
    const trimmed = emailInput.trim().toLowerCase();
    if (!trimmed || authorizedEmails.includes(trimmed)) { setEmailInput(''); return; }
    setAuthorizedEmails(prev => [...prev, trimmed]);
    setEmailInput('');
  }
  function removeEmail(email) { setAuthorizedEmails(prev => prev.filter(e => e !== email)); }

  async function handleSubmit(e) {
    e.preventDefault(); setError(''); setLoading(true);
    const { error } = await supabase.from('clients').insert({ ...form, authorized_emails: authorizedEmails });
    setLoading(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setCreated(true);
  }

  if (created) {
    return (
      <div style={S.form}>
        <p style={S.successMsg}>Client created successfully.</p>
        <button style={S.btnSecondary} onClick={onCreated}>Done</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={S.form}>
      <h3 style={S.sectionHead}>New Client</h3>
      <Field label="Company Name">
        <input style={S.input} required value={form.company_name}
          onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} />
      </Field>
      <Field label="Trade">
        <select style={S.input} value={form.trade}
          onChange={e => setForm(f => ({ ...f, trade: e.target.value }))}>
          {TRADES.map(t => <option key={t}>{t}</option>)}
        </select>
      </Field>
      <Field label="County">
        <input style={S.input} required value={form.county}
          onChange={e => setForm(f => ({ ...f, county: e.target.value }))} />
      </Field>
      <Field label="Website URL">
        <input style={S.input} type="url" placeholder="https://example.com" value={form.website_url}
          onChange={e => setForm(f => ({ ...f, website_url: e.target.value }))} />
      </Field>
      <Field label="Authorized Emails">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {authorizedEmails.map(email => (
            <div key={email} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-input)', border: '1px solid var(--border-strong)', borderRadius: 5 }}>
              <span style={{ flex: 1, fontSize: 13, color: 'var(--text-muted)' }}>{email}</span>
              <button type="button" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 13, padding: 0, fontFamily: FONT }}
                onClick={() => removeEmail(email)}>Remove</button>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 6 }}>
            <input type="email" style={{ ...S.input, flex: 1 }} placeholder="client@example.com"
              value={emailInput} onChange={e => setEmailInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addEmail(e); } }} />
            <button type="button" style={S.btnSecondary} onClick={addEmail}>Add</button>
          </div>
        </div>
      </Field>
      {error && <p style={S.error}>{error}</p>}
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button type="submit" style={S.btnPrimary} disabled={loading}>
          {loading ? 'Saving…' : 'Create Client'}
        </button>
        <button type="button" style={S.btnSecondary} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

// ── AEO Audit Form (Import JSON / Manual / Trigger) ─────────────────────────
function AuditForm({ client, existingAudit, onSuccess }) {
  const isEdit = !!existingAudit;
  const [aeoData, setAeoData] = useState(() => isEdit ? {
    geo_score: existingAudit.geo_score ?? existingAudit.overall_score ?? '',
    citability_score: existingAudit.citability_score ?? '',
    brand_authority_score: existingAudit.brand_authority_score ?? '',
    content_eeat_score: existingAudit.content_eeat_score ?? '',
    technical_geo_score: existingAudit.technical_geo_score ?? '',
    schema_score: existingAudit.schema_score ?? '',
    platform_score: existingAudit.platform_score ?? '',
    platforms: existingAudit.platforms ?? null,
    findings: existingAudit.findings ?? null,
    action_plan: existingAudit.action_plan ?? null,
    crawler_access: existingAudit.crawler_access ?? null,
    business_type: existingAudit.business_type ?? '',
    pages_analyzed: existingAudit.pages_analyzed ?? '',
    executive_summary: existingAudit.executive_summary ?? '',
    notes: existingAudit.notes ?? '',
  } : {
    geo_score: '', citability_score: '', brand_authority_score: '', content_eeat_score: '',
    technical_geo_score: '', schema_score: '', platform_score: '',
    platforms: null, findings: null, action_plan: null, crawler_access: null,
    business_type: '', pages_analyzed: '', executive_summary: '', notes: '',
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [runningAudit, setRunningAudit] = useState(false);
  const [auditType, setAuditType] = useState('full');
  const [auditStatus, setAuditStatus] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  function handleJsonImport(e) {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const raw = JSON.parse(reader.result);
        const parsed = parseAeoJson(raw);
        setAeoData(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(parsed).filter(([, v]) => v != null)),
        }));
      } catch (err) {
        setError(irisError('IRIS-203', err.message));
      }
    };
    reader.readAsText(f);
  }

  async function triggerRemoteAudit() {
    if (!client.website_url) { setError(irisError('IRIS-200')); return; }
    setRunningAudit(true); setError(''); setAuditStatus('queued');
    try {
      const res = await fetch(`${AUDIT_API_URL}/api/run-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ client_id: client.id, url: client.website_url, type: auditType }),
      });
      if (!res.ok) throw new Error(`Server error ${res.status}`);
      const { job_id } = await res.json();

      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const sr = await fetch(`${AUDIT_API_URL}/api/audit-status/${job_id}`);
          const st = await sr.json();
          setAuditStatus(st.status);
          if (st.status === 'complete') {
            clearInterval(pollRef.current); pollRef.current = null;
            setRunningAudit(false);
            setSuccess(true);
            setAuditStatus('');
            if (onSuccess) onSuccess();
          } else if (st.status === 'failed') {
            clearInterval(pollRef.current); pollRef.current = null;
            setRunningAudit(false);
            setError(irisError('IRIS-205', st.error));
            setAuditStatus('');
          }
        } catch { /* keep polling */ }
      }, 5000);

    } catch (err) {
      setError(irisError('IRIS-201', err.message));
      setRunningAudit(false);
      setAuditStatus('');
    }
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError(''); setSuccess(false); setLoading(true);
    let pdf_url = isEdit ? existingAudit.pdf_url : null;
    if (file) {
      const path = `${client.id}/${Date.now()}.pdf`;
      const { error: uploadError } = await supabase.storage.from('audits').upload(path, file, { contentType: 'application/pdf' });
      if (uploadError) { setError(irisError('IRIS-302', uploadError.message)); setLoading(false); return; }
      const { data } = supabase.storage.from('audits').getPublicUrl(path);
      pdf_url = data.publicUrl;
    }
    const payload = {
      client_id: client.id,
      audit_type: auditType,
      geo_score: aeoData.geo_score ? Number(aeoData.geo_score) : null,
      overall_score: aeoData.geo_score ? Number(aeoData.geo_score) : null,
      citability_score: aeoData.citability_score ? Number(aeoData.citability_score) : null,
      brand_authority_score: aeoData.brand_authority_score ? Number(aeoData.brand_authority_score) : null,
      content_eeat_score: aeoData.content_eeat_score ? Number(aeoData.content_eeat_score) : null,
      technical_geo_score: aeoData.technical_geo_score ? Number(aeoData.technical_geo_score) : null,
      schema_score: aeoData.schema_score ? Number(aeoData.schema_score) : null,
      platform_score: aeoData.platform_score ? Number(aeoData.platform_score) : null,
      platforms: aeoData.platforms,
      findings: aeoData.findings,
      action_plan: aeoData.action_plan,
      crawler_access: aeoData.crawler_access,
      business_type: aeoData.business_type || null,
      pages_analyzed: aeoData.pages_analyzed ? Number(aeoData.pages_analyzed) : null,
      executive_summary: aeoData.executive_summary || null,
      notes: aeoData.notes,
      pdf_url,
      published: true,
    };
    let dbError;
    if (isEdit) { const { error } = await supabase.from('audits').update(payload).eq('id', existingAudit.id); dbError = error; }
    else { const { error } = await supabase.from('audits').insert({ ...payload, uploaded_at: new Date().toISOString() }); dbError = error; }
    setLoading(false);
    if (dbError) { setError(irisError('IRIS-301', dbError.message)); return; }
    setSuccess(true);
    if (onSuccess) onSuccess();
  }

  const aCard = { background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 20, marginBottom: 16 };
  const scoreField = (label, key) => (
    <Field label={`${label} (0–100)`} key={key}>
      <input type="number" min={0} max={100} style={S.input}
        value={aeoData[key]} onChange={e => setAeoData(d => ({ ...d, [key]: e.target.value }))} />
    </Field>
  );

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <label style={{ ...S.btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          Import Audit JSON
          <input type="file" accept=".json" onChange={handleJsonImport} style={{ display: 'none' }} />
        </label>
        <label style={{ ...S.btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Upload PDF
          <input type="file" accept=".pdf" onChange={e => setFile(e.target.files[0])} style={{ display: 'none' }} />
        </label>
        {file && <span style={{ fontSize: 12, color: 'var(--text-dim)', alignSelf: 'center' }}>{file.name}</span>}
        <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', marginLeft: 'auto' }}>
          {['full', 'quick'].map(t => (
            <button key={t} type="button" onClick={() => setAuditType(t)} disabled={runningAudit}
              style={{
                padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: runningAudit ? 'default' : 'pointer',
                background: auditType === t ? 'var(--accent)' : 'transparent',
                color: auditType === t ? '#fff' : 'var(--text-muted)',
                border: 'none', textTransform: 'capitalize',
              }}>
              {t === 'full' ? 'Full (Sonnet)' : 'Quick (Haiku)'}
            </button>
          ))}
        </div>
        <button type="button" style={{ ...S.btnPrimary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
          disabled={runningAudit} onClick={triggerRemoteAudit}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
          {runningAudit ? 'Running…' : 'Run AEO Audit'}
        </button>
      </div>
      {auditStatus && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, padding: '10px 14px', background: 'var(--bg-btn)', borderRadius: 10, fontSize: 13 }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
            background: auditStatus === 'running' ? '#f59e0b' : auditStatus === 'queued' ? 'var(--text-dim)' : 'var(--accent)',
            animation: auditStatus === 'running' ? 'pulse 1.5s infinite' : 'none' }} />
          <span style={{ color: 'var(--text-muted)', fontWeight: 500 }}>
            {auditStatus === 'queued' && 'Audit queued — waiting for server…'}
            {auditStatus === 'running' && `Running ${auditType} audit with ${auditType === 'full' ? 'Sonnet' : 'Haiku'}…`}
          </span>
        </div>
      )}

      {/* AEO Score */}
      <div style={aCard}>
        {scoreField('AEO Score', 'geo_score')}
      </div>

      {/* Category Scores — 2-column grid */}
      <div style={aCard}>
        <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' }}>AEO Category Scores</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          {AEO_CATEGORIES.map(c => scoreField(c.label, c.key))}
        </div>
      </div>

      {/* Read-only imported data preview */}
      {aeoData.platforms && (
        <div style={aCard}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' }}>Platform Scores (from import)</p>
          {Object.entries(aeoData.platforms).map(([name, score]) => (
            <div key={name} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--bg-btn)' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{name}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{score}/100</span>
            </div>
          ))}
        </div>
      )}

      {aeoData.findings && Array.isArray(aeoData.findings) && aeoData.findings.length > 0 && (
        <div style={aCard}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' }}>
            Findings ({aeoData.findings.length})
          </p>
          {aeoData.findings.slice(0, 5).map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid var(--bg-btn)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: SEVERITY_COLORS[f.severity] || 'var(--text-dim)', flexShrink: 0 }}>{f.severity}</span>
              <span style={{ fontSize: 13, color: 'var(--text)' }}>{f.title}</span>
            </div>
          ))}
          {aeoData.findings.length > 5 && <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '8px 0 0 0' }}>+ {aeoData.findings.length - 5} more</p>}
        </div>
      )}

      {/* Meta fields */}
      <div style={aCard}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 16px' }}>
          <Field label="Business Type">
            <input style={S.input} value={aeoData.business_type}
              onChange={e => setAeoData(d => ({ ...d, business_type: e.target.value }))} />
          </Field>
          <Field label="Pages Analyzed">
            <input type="number" style={S.input} value={aeoData.pages_analyzed}
              onChange={e => setAeoData(d => ({ ...d, pages_analyzed: e.target.value }))} />
          </Field>
        </div>
      </div>

      {/* Executive Summary + Notes */}
      <div style={aCard}>
        <Field label="Executive Summary">
          <textarea style={{ ...S.input, height: 80, resize: 'vertical' }}
            value={aeoData.executive_summary} onChange={e => setAeoData(d => ({ ...d, executive_summary: e.target.value }))} />
        </Field>
        <div style={{ marginTop: 12 }}>
          <Field label="Notes">
            <textarea style={{ ...S.input, height: 60, resize: 'vertical' }}
              value={aeoData.notes} onChange={e => setAeoData(d => ({ ...d, notes: e.target.value }))} />
          </Field>
        </div>
      </div>

      {error && <p style={S.error}>{error}</p>}
      {success && <p style={S.successMsg}>{isEdit ? 'Audit updated.' : 'Audit saved successfully.'}</p>}
      <button type="submit" style={{ ...S.btnPrimary, width: '100%', padding: '12px 24px' }} disabled={loading}>
        {loading ? (isEdit ? 'Saving…' : 'Saving…') : (isEdit ? 'Save Changes' : 'Save Audit')}
      </button>
    </form>
  );
}

// ── Tasks Tab (paste-parser + grouped list) ──────────────────────────────────
function TasksTab({ client }) {
  const [tasks, setTasks] = useState([]);
  const [latestAuditId, setLatestAuditId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [pasteText, setPasteText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [importing, setImporting] = useState(false);

  const [newTask, setNewTask] = useState('');
  const [newPillar, setNewPillar] = useState('General');
  const [adding, setAdding] = useState(false);

  /** @type {null | { id: string, description: string, pillar: string, notes: string|null, status: string }} */
  const [notesModalTask, setNotesModalTask] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  const [colorPickerTaskId, setColorPickerTaskId] = useState(null);

  useEffect(() => {
    if (!colorPickerTaskId) return;
    function handleClick() { setColorPickerTaskId(null); }
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [colorPickerTaskId]);

  async function setTaskColor(task, color) {
    const newDesc = encodeTaskDesc(task.description, task.pillar, task.notes || '', color);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, tagColor: color } : t));
    setColorPickerTaskId(null);
    await supabase.from('tasks').update({ description: newDesc }).eq('id', task.id);
  }

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: taskData }, { data: auditData }] = await Promise.all([
      supabase.from('tasks').select('*').eq('client_id', client.id).order('created_at', { ascending: false }),
      supabase.from('audits').select('id').eq('client_id', client.id).order('uploaded_at', { ascending: false }).limit(1),
    ]);
    setTasks((taskData || []).map(decodeTaskRow).filter(Boolean));
    setLatestAuditId(auditData?.[0]?.id || null);
    setLoading(false);
  }, [client.id]);

  useEffect(() => { load(); }, [load]);

  function handleParse() {
    const items = parseAuditBreakdown(pasteText);
    if (items.length === 0) { setError(irisError('IRIS-203', 'No tasks found. Make sure lines start with a check or X mark.')); return; }
    setParsed(items);
  }

  async function handleImport() {
    if (!parsed?.length) return;
    setImporting(true); setError('');
    const now = new Date().toISOString();
    const rows = parsed.map(t => ({
      client_id: client.id, audit_id: latestAuditId,
      description: encodeTaskDesc(t.description, t.pillar),
      completed_at: t.status === 'done' ? now : null, created_at: now,
    }));
    const { error } = await supabase.from('tasks').insert(rows);
    setImporting(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setParsed(null); setPasteText('');
    load();
  }

  async function toggleStatus(task) {
    const newStatus = task.status === 'done' ? 'todo' : 'done';
    const newCompleted = newStatus === 'done' ? new Date().toISOString() : null;
    await supabase.from('tasks').update({ completed_at: newCompleted }).eq('id', task.id);
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, status: newStatus, completed_at: newCompleted } : t));
  }

  function openNotesModal(task) {
    setNotesModalTask(task);
    setNotesDraft(task.notes || '');
  }

  function closeNotesModal() {
    setNotesModalTask(null);
    setNotesDraft('');
  }

  async function saveTaskNotes() {
    if (!notesModalTask) return;
    const task = tasks.find(t => t.id === notesModalTask.id);
    if (!task) return;
    setNotesSaving(true);
    const newDesc = encodeTaskDesc(task.description, task.pillar, notesDraft || '', task.tagColor);
    const { error } = await supabase.from('tasks').update({ description: newDesc }).eq('id', task.id);
    setNotesSaving(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setTasks(prev => prev.map(t => t.id === task.id ? { ...t, notes: notesDraft || null } : t));
    closeNotesModal();
  }

  useEffect(() => {
    if (!notesModalTask) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') closeNotesModal();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [notesModalTask]);

  async function deleteTask(id) {
    if (notesModalTask?.id === id) closeNotesModal();
    await supabase.from('tasks').delete().eq('id', id);
    setTasks(prev => prev.filter(t => t.id !== id));
  }

  async function addTask(e) {
    e.preventDefault();
    if (!newTask.trim()) return;
    setAdding(true); setError('');
    const { error } = await supabase.from('tasks').insert({
      client_id: client.id, audit_id: latestAuditId,
      description: encodeTaskDesc(newTask.trim(), newPillar),
      created_at: new Date().toISOString(),
    });
    setAdding(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setNewTask('');
    load();
  }

  async function markAllDone() {
    const now = new Date().toISOString();
    await supabase.from('tasks').update({ completed_at: now })
      .eq('client_id', client.id).is('completed_at', null);
    load();
  }

  async function deleteAllDone() {
    if (!window.confirm('Delete all completed tasks? This cannot be undone.')) return;
    await supabase.from('tasks').delete().eq('client_id', client.id).not('completed_at', 'is', null);
    load();
  }

  const grouped = {};
  for (const t of tasks) {
    const p = t.pillar || 'General';
    if (!grouped[p]) grouped[p] = [];
    grouped[p].push(t);
  }
  const pillarOrder = [...new Set(tasks.map(t => t.pillar || 'General'))];
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'done').length;
  const progressPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  const [showImport, setShowImport] = useState(false);
  const [copyFlash, setCopyFlash] = useState('');
  const [showColorCopy, setShowColorCopy] = useState(false);

  useEffect(() => {
    if (!showColorCopy) return;
    function dismiss() { setShowColorCopy(false); }
    document.addEventListener('click', dismiss);
    return () => document.removeEventListener('click', dismiss);
  }, [showColorCopy]);

  const tCard = { background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 28 };

  const todoTasks = tasks.filter(t => t.status !== 'done');

  function formatTasksForCopy(list) {
    return list.map(t => {
      let line = `Task: ${t.description}`;
      if (t.notes && String(t.notes).trim()) line += `\nNotes: ${String(t.notes).trim()}`;
      return line;
    }).join('\n\n---\n\n');
  }

  async function copyTasks(list, label) {
    if (list.length === 0) return;
    try {
      await navigator.clipboard.writeText(formatTasksForCopy(list));
      setCopyFlash(label);
      setTimeout(() => setCopyFlash(''), 2000);
    } catch {
      setError(irisError('IRIS-500', 'Clipboard access denied. Use HTTPS or allow clipboard access.'));
      setTimeout(() => setError(''), 4000);
    }
  }

  const usedColors = [...new Set(todoTasks.map(t => t.tagColor).filter(Boolean))];

  return (
    <div>
      {/* Top row — stats + add */}
      <div style={{ display: 'grid', gridTemplateColumns: totalTasks > 0 ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 24 }}>
        {totalTasks > 0 && (
          <div style={tCard}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
              <span style={{ fontSize: 32, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1 }}>{progressPct}%</span>
              <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>complete</span>
            </div>
            <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ height: '100%', width: progressPct + '%', background: progressPct === 100 ? 'var(--success)' : 'var(--accent)', borderRadius: 999, transition: 'width 0.3s' }} />
            </div>
            <div style={{ display: 'flex', gap: 16 }}>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{doneTasks}</p>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '2px 0 0 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Done</p>
              </div>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{totalTasks - doneTasks}</p>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '2px 0 0 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Remaining</p>
              </div>
              <div>
                <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{pillarOrder.length}</p>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '2px 0 0 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Pillars</p>
              </div>
            </div>
            {/* Bulk actions inline */}
            {totalTasks > 0 && (
              <div style={{ display: 'flex', gap: 8, marginTop: 16, borderTop: '1px solid var(--border-subtle)', paddingTop: 16 }}>
                {tasks.some(t => t.status === 'todo') && (
                  <button style={{ ...S.btnSecondary, fontSize: 12, padding: '6px 12px' }} onClick={markAllDone}>Mark All Done</button>
                )}
                {tasks.some(t => t.status === 'done') && (
                  <button style={{ ...S.btnDanger, fontSize: 12, padding: '6px 12px' }} onClick={deleteAllDone}>Clear Done</button>
                )}
              </div>
            )}
          </div>
        )}

        <div style={tCard}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' }}>Add Task</p>
          <form onSubmit={addTask} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input style={S.input} placeholder="What needs to be done?"
              value={newTask} onChange={e => setNewTask(e.target.value)} />
            <div style={{ display: 'flex', gap: 8 }}>
              <select style={{ ...S.input, flex: 1 }}
                value={newPillar} onChange={e => setNewPillar(e.target.value)}>
                {PILLAR_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <button type="submit" style={{ ...S.btnPrimary, flexShrink: 0 }} disabled={adding}>
                {adding ? '…' : 'Add Task'}
              </button>
            </div>
          </form>
          <div style={{ borderTop: '1px solid var(--border-subtle)', marginTop: 16, paddingTop: 12 }}>
            <button onClick={() => setShowImport(v => !v)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, fontSize: 13, color: 'var(--accent)', padding: 0, fontWeight: 500 }}>
              {showImport ? 'Hide Import' : 'Import from Audit Breakdown'}
            </button>
            {showImport && (
              <div style={{ marginTop: 12 }}>
                <textarea style={{ ...S.input, height: 100, resize: 'vertical', fontFamily: "'SF Mono', SFMono-Regular, Menlo, monospace", fontSize: 12, lineHeight: 1.5 }}
                  placeholder="Paste audit breakdown here…"
                  value={pasteText} onChange={e => { setPasteText(e.target.value); setParsed(null); }} />
                {pasteText.trim() && !parsed && (
                  <button style={{ ...S.btnPrimary, marginTop: 8, fontSize: 13, padding: '8px 16px' }} onClick={handleParse}>Parse</button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Parse Preview */}
      {parsed && (
        <div style={{ ...tCard, marginBottom: 24, borderColor: 'color-mix(in srgb, var(--accent) 20%, transparent)' }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: '0 0 16px 0' }}>
            Found {parsed.length} tasks — {parsed.filter(t => t.status === 'done').length} done, {parsed.filter(t => t.status === 'todo').length} to-do
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {Object.entries(parsed.reduce((acc, t) => { (acc[t.pillar] = acc[t.pillar] || []).push(t); return acc; }, {})).map(([pillar, items]) => (
              <div key={pillar} style={{ padding: 16, background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border-subtle)' }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{pillar}</p>
                {items.map((t, i) => (
                  <p key={i} style={{ fontSize: 13, color: t.status === 'done' ? 'var(--text-dim)' : 'var(--text-muted)', margin: '4px 0', display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                    <span style={{ color: t.status === 'done' ? 'var(--success)' : 'var(--danger)', flexShrink: 0, marginTop: 1 }}>{t.status === 'done' ? '✓' : '✗'}</span>
                    <span>{t.description}</span>
                  </p>
                ))}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button style={S.btnPrimary} onClick={handleImport} disabled={importing}>
              {importing ? 'Importing…' : `Import ${parsed.length} Tasks`}
            </button>
            <button style={S.btnSecondary} onClick={() => { setParsed(null); setPasteText(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {error && <p style={{ ...S.error, marginBottom: 16 }}>{error}</p>}

      {/* Task list by pillar */}
      {loading ? (
        <p style={S.muted}><LoadingDots /></p>
      ) : tasks.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <p style={{ fontSize: 15, color: 'var(--text-dim)', margin: '0 0 4px 0' }}>No tasks yet</p>
          <p style={{ fontSize: 13, color: 'var(--text-faint)', margin: 0 }}>Add a task above or import from an audit breakdown.</p>
        </div>
      ) : (
        <>
        <div style={{ display: 'grid', gridTemplateColumns: pillarOrder.length >= 3 ? 'repeat(auto-fill, minmax(420px, 1fr))' : '1fr', gap: 16 }}>
          {pillarOrder.map(pillar => {
            const items = grouped[pillar];
            const pDone = items.filter(t => t.status === 'done').length;
            return (
              <div key={pillar} style={tCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <p style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{pillar}</p>
                  <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{pDone}/{items.length}</span>
                </div>
                <div style={{ height: 3, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden', marginBottom: 14 }}>
                  <div style={{ height: '100%', width: (items.length > 0 ? Math.round((pDone / items.length) * 100) : 0) + '%', background: 'var(--success)', borderRadius: 999, transition: 'width 0.3s' }} />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {items.map(t => (
                    <div key={t.id}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 0, padding: '10px 12px',
                        borderRadius: 10, transition: 'background 0.15s',
                        background: notesModalTask?.id === t.id ? 'color-mix(in srgb, var(--accent) 8%, transparent)' : 'transparent',
                      }}>
                        {/* Tag color dot */}
                        <div style={{ position: 'relative', flexShrink: 0, marginRight: 8 }}>
                          <button type="button"
                            onClick={e => { e.stopPropagation(); setColorPickerTaskId(colorPickerTaskId === t.id ? null : t.id); }}
                            title="Set tag color"
                            style={{
                              width: 14, height: 14, borderRadius: 7, border: t.tagColor ? 'none' : '2px dashed var(--border-dashed)',
                              background: t.tagColor || 'transparent', cursor: 'pointer', padding: 0,
                              transition: 'transform 0.15s, box-shadow 0.15s',
                              boxShadow: t.tagColor ? `0 0 6px ${t.tagColor}44` : 'none',
                            }}
                          />
                          {colorPickerTaskId === t.id && (
                            <div style={{
                              position: 'absolute', top: 22, left: -4, zIndex: 50,
                              background: 'var(--bg-elevated)', border: '1px solid var(--border-medium)',
                              borderRadius: 10, padding: 8, display: 'flex', gap: 6, flexWrap: 'wrap', width: 120,
                    boxShadow: 'var(--shadow-medium)',
                  }} onClick={e => e.stopPropagation()}>
                              {TAG_COLORS.map(tc => (
                                <button key={tc.value} type="button" title={tc.label}
                                  onClick={() => setTaskColor(t, tc.value)}
                                  style={{
                                    width: 22, height: 22, borderRadius: 11, padding: 0, cursor: 'pointer',
                                    border: tc.value === t.tagColor ? '2px solid var(--text)' : tc.value ? 'none' : '2px dashed var(--border-dashed)',
                                    background: tc.value || 'transparent',
                                    transition: 'transform 0.1s',
                                  }}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        <input type="checkbox" checked={t.status === 'done'} onChange={() => toggleStatus(t)}
                          onClick={e => e.stopPropagation()}
                          style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0, accentColor: 'var(--accent)', marginRight: 10 }} />
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={() => openNotesModal(t)}
                          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openNotesModal(t); } }}
                          title="Click to open notes"
                          style={{
                            flex: 1, fontSize: 13, lineHeight: 1.4,
                            color: t.status === 'done' ? 'var(--text-faint)' : 'var(--text)',
                            textDecoration: t.status === 'done' ? 'line-through' : 'none',
                            cursor: 'pointer', textAlign: 'left',
                          }}
                        >{t.description}</span>
                        <button type="button" onClick={e => { e.stopPropagation(); openNotesModal(t); }}
                          title={t.notes ? 'View or edit notes' : 'Add notes'}
                          aria-label={t.notes ? 'Task has notes — click to edit' : 'Add notes'}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: '2px 6px',
                            color: t.notes ? 'var(--accent)' : 'var(--icon-idle)', fontSize: 13, fontFamily: FONT,
                            borderRadius: 6, transition: 'color 0.15s',
                          }}>
                          {t.notes ? '✎' : '＋'}
                        </button>
                        <button type="button" onClick={e => { e.stopPropagation(); deleteTask(t.id); }} title="Delete"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--icon-idle)', fontSize: 14, padding: '2px 6px', fontFamily: FONT, borderRadius: 6 }}>
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {tasks.length > 0 && (
          <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <button type="button"
              onClick={() => copyTasks(todoTasks, 'all')}
              disabled={todoTasks.length === 0}
              style={{
                ...S.btnSecondary,
                opacity: todoTasks.length === 0 ? 0.45 : 1,
                cursor: todoTasks.length === 0 ? 'not-allowed' : 'pointer',
              }}>
              {copyFlash === 'all' ? 'Copied!' : `Copy all to-do (${todoTasks.length})`}
            </button>

            {usedColors.length > 0 && (
              <div style={{ position: 'relative' }}>
                <button type="button"
                  onClick={(e) => { e.stopPropagation(); setShowColorCopy(v => !v); }}
                  style={S.btnSecondary}>
                  {copyFlash && copyFlash !== 'all' ? 'Copied!' : 'Copy by color'}
                </button>
                {showColorCopy && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, zIndex: 50,
                    background: 'var(--bg-elevated)', border: '1px solid var(--border-medium)',
                    borderRadius: 10, padding: 10, display: 'flex', gap: 8,
                    boxShadow: 'var(--shadow-medium)',
                  }} onClick={e => e.stopPropagation()}>
                    {usedColors.map(color => {
                      const matching = todoTasks.filter(t => t.tagColor === color);
                      const label = TAG_COLORS.find(tc => tc.value === color)?.label || '';
                      return (
                        <button key={color} type="button"
                          title={`Copy ${matching.length} ${label} task${matching.length !== 1 ? 's' : ''}`}
                          onClick={() => { copyTasks(matching, color); setShowColorCopy(false); }}
                          style={{
                            width: 28, height: 28, borderRadius: 14, border: 'none',
                            background: color, cursor: 'pointer', position: 'relative',
                          }}>
                          <span style={{
                            position: 'absolute', bottom: -16, left: '50%', transform: 'translateX(-50%)',
                            fontSize: 10, color: 'var(--text-muted)', whiteSpace: 'nowrap',
                          }}>{matching.length}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>
              {todoTasks.length} incomplete task{todoTasks.length !== 1 ? 's' : ''}
            </span>
          </div>
        )}
        </>
      )}

      {notesModalTask && (
        <div
          role="presentation"
          style={S.dialogOverlay}
          onClick={e => { if (e.target === e.currentTarget) closeNotesModal(); }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="task-notes-title"
            style={{ ...S.dialog, maxWidth: 720, width: 'min(720px, 92vw)' }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
              <div style={{ minWidth: 0 }}>
                <p id="task-notes-title" style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 6px 0' }}>
                  Task notes
                </p>
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', margin: 0, lineHeight: 1.35 }}>
                  {notesModalTask.description}
                </p>
                <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '6px 0 0 0' }}>{notesModalTask.pillar}</p>
              </div>
              <button type="button" onClick={closeNotesModal} title="Close"
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 22, lineHeight: 1, padding: 4, fontFamily: FONT }}>
                ×
              </button>
            </div>
            <textarea
              style={{ ...S.input, minHeight: 280, resize: 'vertical', fontSize: 14, lineHeight: 1.55, marginBottom: 16 }}
              placeholder="Add internal notes for this task…"
              value={notesDraft}
              onChange={e => setNotesDraft(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" style={S.btnSecondary} onClick={closeNotesModal} disabled={notesSaving}>Cancel</button>
              <button type="button" style={S.btnPrimary} onClick={saveTaskNotes} disabled={notesSaving}>
                {notesSaving ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Audit History Section (AEO) ──────────────────────────────────────────────
function AuditHistorySection({ client }) {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAudit, setEditingAudit] = useState(null);
  const [viewingAudit, setViewingAudit] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('audits').select('*').eq('client_id', client.id)
      .order('uploaded_at', { ascending: false });
    setAudits(data || []); setLoading(false);
  }, [client.id]);

  useEffect(() => { load(); }, [load]);

  async function deleteAudit(audit) {
    setDeletingId(audit.id); setError('');
    const { error: taskErr } = await supabase.from('tasks').update({ audit_id: null }).eq('audit_id', audit.id);
    if (taskErr) { setDeletingId(null); setError(irisError('IRIS-303', taskErr.message)); return; }
    const { error } = await supabase.from('audits').delete().eq('id', audit.id);
    if (error) { setDeletingId(null); setError(irisError('IRIS-303', error.message)); return; }
    const path = auditsBucketPathFromPdfUrl(audit.pdf_url);
    if (path) {
      const { error: storageErr } = await supabase.storage.from('audits').remove([path]);
      if (storageErr) console.warn('[AuditHistory] Could not remove PDF from storage:', storageErr.message);
    }
    setDeletingId(null); load();
  }

  if (editingAudit) {
    return (
      <div>
        <button style={{ ...S.btnSecondary, marginBottom: 16 }}
          onClick={() => setEditingAudit(null)}>← Back to Audit History</button>
        <AuditForm client={client} existingAudit={editingAudit}
          onSuccess={() => { setEditingAudit(null); load(); }} />
      </div>
    );
  }

  if (viewingAudit) {
    return <AuditDetailView audit={viewingAudit} onBack={() => setViewingAudit(null)} />;
  }

  return (
    <div>
      {error && <p style={{ ...S.error, marginBottom: 12 }}>{error}</p>}
      {loading ? (
        <p style={S.muted}><LoadingDots /></p>
      ) : audits.length === 0 ? (
        <p style={S.muted}>No audits yet.</p>
      ) : (
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, overflow: 'hidden' }}>
          {audits.map((a, i) => {
            const score = a.geo_score ?? a.overall_score ?? 0;
            const typeBadge = a.audit_type === 'quick'
              ? { bg: 'rgba(10,132,255,0.12)', color: '#0A84FF', text: 'Quick' }
              : { bg: 'color-mix(in srgb, var(--accent) 12%, transparent)', color: 'var(--accent)', text: 'Full' };
            return (
              <div key={a.id} style={{
                display: 'grid', gridTemplateColumns: '100px auto 1fr 60px auto auto auto', gap: 10, alignItems: 'center',
                padding: '12px 20px', borderBottom: i < audits.length - 1 ? '1px solid var(--bg-btn)' : 'none',
                cursor: 'pointer',
              }} onClick={() => setViewingAudit(a)}>
                <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>{formatDate(a.uploaded_at)}</span>
                <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6, background: typeBadge.bg, color: typeBadge.color }}>{typeBadge.text}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: score + '%', background: 'var(--accent)', borderRadius: 999 }} />
                  </div>
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>{score}%</span>
                <button style={{ ...S.btnSecondary, fontSize: 12, padding: '5px 12px' }} disabled={deletingId != null}
                  onClick={e => { e.stopPropagation(); setEditingAudit(a); }}>Edit</button>
                <button style={{ ...S.btnDanger, fontSize: 12, padding: '5px 12px' }} disabled={deletingId != null}
                  onClick={e => { e.stopPropagation(); if (window.confirm(`Delete audit from ${formatDate(a.uploaded_at)}?`)) deleteAudit(a); }}>
                  {deletingId === a.id ? '…' : 'Delete'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Audit Detail View (AEO) ─────────────────────────────────────────────────
function AuditDetailView({ audit, onBack }) {
  const a = audit;
  const score = a.geo_score ?? a.overall_score ?? 0;
  const dCard = { background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 20, marginBottom: 16 };
  const dLabel = { fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' };

  const findingsBySeverity = {};
  if (a.findings && Array.isArray(a.findings)) {
    for (const f of a.findings) {
      const sev = f.severity || 'medium';
      if (!findingsBySeverity[sev]) findingsBySeverity[sev] = [];
      findingsBySeverity[sev].push(f);
    }
  }

  return (
    <div>
      <button style={{ ...S.btnSecondary, marginBottom: 16 }} onClick={onBack}>← Back to Audit History</button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 24 }}>
        <span style={{ fontSize: 48, fontWeight: 700, color: 'var(--text)' }}>{score}<span style={{ fontSize: 20, color: 'var(--text-dim)' }}>%</span></span>
        <span style={{ fontSize: 15, color: 'var(--text-dim)' }}>AEO Score · {formatDate(a.uploaded_at)}</span>
        {a.business_type && <span style={{ fontSize: 12, padding: '3px 10px', borderRadius: 6, background: 'var(--bg-btn)', color: 'var(--text-muted)' }}>{a.business_type}</span>}
        {a.pdf_url && (
          <a href={a.pdf_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500, textDecoration: 'none' }}>
            Download PDF ↗
          </a>
        )}
      </div>

      {a.executive_summary && (
        <div style={dCard}>
          <p style={dLabel}>Executive Summary</p>
          <p style={{ fontSize: 14, color: 'var(--text)', lineHeight: 1.6, margin: 0 }}>{a.executive_summary}</p>
        </div>
      )}

      {/* Category Score Bars */}
      <div style={dCard}>
        <p style={dLabel}>AEO Category Breakdown</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {AEO_CATEGORIES.map(cat => {
            const val = a[cat.key] ?? 0;
            return (
              <div key={cat.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{cat.label} <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>({cat.weight})</span></span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{val}/100</span>
                </div>
                <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: val + '%', background: cat.color, borderRadius: 999, transition: 'width 0.3s' }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Platforms */}
      {a.platforms && typeof a.platforms === 'object' && (
        <div style={dCard}>
          <p style={dLabel}>Platform Scores</p>
          {Object.entries(a.platforms).map(([name, val]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--bg-btn)' }}>
              <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 160, flexShrink: 0 }}>{name}</span>
              <div style={{ flex: 1, height: 5, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: val + '%', background: 'var(--accent)', borderRadius: 999 }} />
              </div>
              <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', width: 40, textAlign: 'right' }}>{val}</span>
            </div>
          ))}
        </div>
      )}

      {/* Findings by severity */}
      {Object.keys(findingsBySeverity).length > 0 && (
        <div style={dCard}>
          <p style={dLabel}>Findings</p>
          {['critical', 'high', 'medium', 'low'].filter(s => findingsBySeverity[s]).map(severity => (
            <div key={severity} style={{ marginBottom: 16 }}>
              <p style={{ fontSize: 12, fontWeight: 700, color: SEVERITY_COLORS[severity], textTransform: 'uppercase', margin: '0 0 8px 0' }}>
                {severity} ({findingsBySeverity[severity].length})
              </p>
              {findingsBySeverity[severity].map((f, i) => (
                <div key={i} style={{ padding: '8px 0', borderBottom: '1px solid var(--bg-btn)' }}>
                  <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', margin: 0 }}>{f.title}</p>
                  {f.description && <p style={{ fontSize: 12, color: 'var(--text-dim)', margin: '4px 0 0 0', lineHeight: 1.5 }}>{f.description}</p>}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Action Plan */}
      {a.action_plan && (
        <div style={dCard}>
          <p style={dLabel}>Action Plan</p>
          {['quick_wins', 'medium_term', 'strategic'].map(section => {
            const items = a.action_plan[section];
            if (!items || items.length === 0) return null;
            const sectionLabel = section === 'quick_wins' ? 'Quick Wins' : section === 'medium_term' ? 'Medium-Term' : 'Strategic';
            return (
              <div key={section} style={{ marginBottom: 14 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', margin: '0 0 6px 0' }}>{sectionLabel}</p>
                {items.map((item, i) => (
                  <p key={i} style={{ fontSize: 13, color: 'var(--text)', margin: '0 0 4px 0', paddingLeft: 12 }}>
                    • {typeof item === 'string' ? item : item.title || item.description || JSON.stringify(item)}
                  </p>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {/* Crawler Access */}
      {a.crawler_access && typeof a.crawler_access === 'object' && (
        <div style={dCard}>
          <p style={dLabel}>Crawler Access</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {Object.entries(a.crawler_access).map(([name, status]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                <span style={{ width: 8, height: 8, borderRadius: 4, background: status === true || status === 'allowed' ? 'var(--success)' : 'var(--danger)', flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{name}</span>
                <span style={{ fontSize: 12, color: 'var(--text-dim)', marginLeft: 'auto' }}>{typeof status === 'string' ? status : status ? 'Allowed' : 'Blocked'}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {a.notes && (
        <div style={dCard}>
          <p style={dLabel}>Notes</p>
          <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.6, margin: 0 }}>{a.notes}</p>
        </div>
      )}
    </div>
  );
}

// ── Goal Tab ─────────────────────────────────────────────────────────────────
function GoalTab({ client, onSaved }) {
  const [goalScore, setGoalScore] = useState(client.goal_score != null ? String(client.goal_score) : '');
  const [goalDate, setGoalDate] = useState(client.goal_date ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    const { error } = await supabase.from('clients').update({
      goal_score: goalScore ? Number(goalScore) : null, goal_date: goalDate || null,
    }).eq('id', client.id);
    setSaving(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setSaved(true); setTimeout(() => setSaved(false), 2000);
    if (onSaved) onSaved({ goal_score: goalScore ? Number(goalScore) : null, goal_date: goalDate || null });
  }

  const gCard = { background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24 };
  const currentScore = client.goal_score;
  const hasGoal = currentScore != null || goalDate;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: hasGoal ? '1fr 1fr' : '1fr', gap: 16, marginBottom: 24 }}>
        {/* Current Goal Display */}
        {hasGoal && (
          <div style={gCard}>
            <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px 0' }}>Current Goal</p>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 8 }}>
              <span style={{ fontSize: 40, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1 }}>{client.goal_score ?? '—'}</span>
              <span style={{ fontSize: 16, color: 'var(--text-dim)' }}>/100</span>
            </div>
            {client.goal_date && (
              <p style={{ fontSize: 14, color: 'var(--text-muted)', margin: 0 }}>
                by {new Date(client.goal_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            )}
            {client.goal_date && (() => {
              const now = new Date();
              const target = new Date(client.goal_date + 'T00:00:00');
              const daysLeft = Math.ceil((target - now) / (1000 * 60 * 60 * 24));
              return (
                <div style={{ marginTop: 16, padding: '10px 14px', background: `color-mix(in srgb, ${daysLeft > 30 ? 'var(--success)' : daysLeft > 0 ? 'var(--warning)' : 'var(--danger)'} 6%, transparent)`, borderRadius: 10, border: `1px solid color-mix(in srgb, ${daysLeft > 30 ? 'var(--success)' : daysLeft > 0 ? 'var(--warning)' : 'var(--danger)'} 12%, transparent)` }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: daysLeft > 30 ? 'var(--success)' : daysLeft > 0 ? 'var(--warning)' : 'var(--danger)' }}>
                    {daysLeft > 0 ? daysLeft + ' days remaining' : Math.abs(daysLeft) + ' days overdue'}
                  </span>
                </div>
              );
            })()}
          </div>
        )}

        {/* Edit Form */}
        <div style={gCard}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 16px 0' }}>{hasGoal ? 'Update Goal' : 'Set a Goal'}</p>
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Target Score (0–100)">
              <input type="number" min={0} max={100} style={S.input}
                value={goalScore} onChange={e => setGoalScore(e.target.value)} placeholder="e.g. 75" />
            </Field>
            <Field label="Target Date">
              <input type="date" style={{ ...S.input, colorScheme: 'inherit' }}
                value={goalDate} onChange={e => setGoalDate(e.target.value)} />
            </Field>
            {error && <p style={S.error}>{error}</p>}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button type="submit" style={{ ...S.btnPrimary, width: '100%' }} disabled={saving}>
                {saving ? 'Saving…' : 'Save Goal'}
              </button>
              {saved && <span style={S.savedMsg}>Saved</span>}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ── Notes Tab ────────────────────────────────────────────────────────────────
function NotesTab({ client }) {
  const [notes, setNotes] = useState(client.admin_notes ?? '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  async function handleBlur() {
    setError('');
    const { error } = await supabase.from('clients').update({ admin_notes: notes }).eq('id', client.id);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 4px 0' }}>Internal Notes</p>
          <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: 0 }}>Private — only visible in admin. Auto-saves on blur.</p>
        </div>
        {saved && <span style={{ ...S.savedMsg, fontSize: 12, padding: '4px 12px', background: 'color-mix(in srgb, var(--success) 8%, transparent)', borderRadius: 999 }}>Saved</span>}
      </div>
      <textarea style={{ ...S.input, height: 320, resize: 'vertical', fontFamily: FONT, lineHeight: 1.6, fontSize: 14 }}
        value={notes} onChange={e => setNotes(e.target.value)} onBlur={handleBlur}
        placeholder="Add internal notes about this client…" />
      {error && <p style={{ ...S.error, marginTop: 8 }}>{error}</p>}
    </div>
  );
}

// ── Admin Settings (users) ───────────────────────────────────────────────────
function AdminSettings() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from('admin_users').select('*').order('created_at');
    setAdmins(data || []); setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function addAdmin(e) {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true); setError('');
    const { error } = await supabase.from('admin_users').insert({ email: newEmail.trim() });
    setAdding(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setNewEmail(''); load();
  }

  async function removeAdmin(id) {
    if (!window.confirm('Remove this admin?')) return;
    await supabase.from('admin_users').delete().eq('id', id);
    load();
  }

  return (
    <div>
      <h3 style={{ fontSize: 15, fontWeight: 600, margin: '0 0 20px 0', color: 'var(--text)' }}>Settings</h3>

      {/* Admin Users */}
      <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-dim)', margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Admin Users</h4>
      {loading ? <p style={S.muted}><LoadingDots /></p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {admins.map(a => (
            <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--border)', borderRadius: 12, background: 'var(--bg-btn)' }}>
              <span style={{ flex: 1, fontSize: 14, color: 'var(--text-muted)' }}>{a.email}</span>
              <button style={S.btnDanger} onClick={() => removeAdmin(a.id)}>Remove</button>
            </div>
          ))}
        </div>
      )}
      <form onSubmit={addAdmin} style={{ display: 'flex', gap: 8 }}>
        <input type="email" required style={{ ...S.input, flex: 1 }}
          placeholder="new-admin@example.com" value={newEmail}
          onChange={e => setNewEmail(e.target.value)} />
        <button type="submit" style={S.btnPrimary} disabled={adding}>
          {adding ? 'Adding…' : 'Add Admin'}
        </button>
      </form>
      {error && <p style={{ ...S.error, marginTop: 8 }}>{error}</p>}
    </div>
  );
}

// ── Overdue Banner ───────────────────────────────────────────────────────────
function OverdueBanner({ clients, recentAuditIds, onSelect }) {
  const overdue = clients.filter(c => !recentAuditIds.has(c.id));
  if (!overdue.length) return null;
  return (
    <div style={S.overdueBanner}>
      <span style={{ fontWeight: 600, marginRight: 6 }}>
        {overdue.length} client{overdue.length !== 1 ? 's' : ''} overdue for an audit:
      </span>
      {overdue.map((c, i) => (
        <span key={c.id}>
          <button style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)',
            fontWeight: 600, fontSize: 13, padding: 0, textDecoration: 'underline', fontFamily: FONT,
          }} onClick={() => onSelect(c)}>{c.company_name}</button>
          {i < overdue.length - 1 && ', '}
        </span>
      ))}
    </div>
  );
}

// ── Pending Audits Banner ─────────────────────────────────────────────────────
function PendingAuditsBanner({ audits, clients, onPublish, onDelete, onEdit }) {
  const [publishing, setPublishing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  async function handlePublish(a) {
    setPublishing(a.id);
    await onPublish(a.id);
    setPublishing(null);
  }

  async function handleDelete(a) {
    if (!window.confirm(`Reject and delete this audit for ${clients.find(c => c.id === a.client_id)?.company_name || 'unknown'}?`)) return;
    setDeleting(a.id);
    await onDelete(a.id);
    setDeleting(null);
  }

  return (
    <div style={{
      margin: '0 0 0 0', padding: '16px 24px',
      background: 'color-mix(in srgb, var(--accent) 6%, transparent)', borderBottom: '1px solid color-mix(in srgb, var(--accent) 15%, transparent)',
    }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' }}>
        New Audit{audits.length !== 1 ? 's' : ''} Detected ({audits.length})
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {audits.map(a => {
          const cl = clients.find(c => c.id === a.client_id);
          const busy = publishing === a.id || deleting === a.id;
          return (
            <div key={a.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px',
              background: 'var(--bg-btn)', borderRadius: 12,
              border: '1px solid var(--border-subtle)',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
                  {cl?.company_name || 'Unknown'}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-dim)', marginLeft: 10 }}>
                  AEO: {a.geo_score ?? a.overall_score}% · {a.audit_type === 'quick' ? 'Quick' : 'Full'} · {formatDate(a.uploaded_at)}
                </span>
              </div>
              <button
                onClick={() => onEdit(a)}
                disabled={busy}
                style={{ ...S.btnSecondary, fontSize: 12, padding: '6px 12px' }}
              >Review</button>
              <button
                onClick={() => handlePublish(a)}
                disabled={busy}
                style={{ ...S.btnPrimary, fontSize: 12, padding: '6px 14px' }}
              >{publishing === a.id ? 'Publishing…' : 'Publish'}</button>
              <button
                onClick={() => handleDelete(a)}
                disabled={busy}
                style={{ ...S.btnDanger, fontSize: 12, padding: '6px 12px' }}
              >{deleting === a.id ? '…' : 'Reject'}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Authorized Emails Section ────────────────────────────────────────────────
function AuthorizedEmailsSection({ client }) {
  const [emails, setEmails] = useState(client.authorized_emails || []);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function addEmail(e) {
    e.preventDefault();
    const trimmed = newEmail.trim().toLowerCase();
    if (!trimmed || emails.includes(trimmed)) { setNewEmail(''); return; }
    const updated = [...emails, trimmed];
    setSaving(true); setError('');
    const { error } = await supabase.from('clients').update({ authorized_emails: updated }).eq('id', client.id);
    setSaving(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setEmails(updated); setNewEmail('');
  }

  async function removeEmail(email) {
    const updated = emails.filter(e => e !== email);
    setSaving(true); setError('');
    const { error } = await supabase.from('clients').update({ authorized_emails: updated }).eq('id', client.id);
    setSaving(false);
    if (error) { setError(irisError('IRIS-301', error.message)); return; }
    setEmails(updated);
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 20 }}>
      <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: '0 0 16px 0' }}>Users who can log in and view this client's dashboard.</p>
      {emails.length > 0 && (
        <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, border: '1px solid var(--border-subtle)' }}>
          {emails.map((email, i) => (
            <div key={email} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < emails.length - 1 ? '1px solid var(--bg-btn)' : 'none' }}>
              <span style={{ flex: 1, fontSize: 14, color: 'var(--text-muted)' }}>{email}</span>
              <button style={{ ...S.btnDanger, fontSize: 12, padding: '4px 10px' }} disabled={saving} onClick={() => removeEmail(email)}>Remove</button>
            </div>
          ))}
        </div>
      )}
      {emails.length === 0 && <p style={{ ...S.muted, marginBottom: 16 }}>No authorized emails yet.</p>}
      <form onSubmit={addEmail} style={{ display: 'flex', gap: 8 }}>
        <input type="email" required style={{ ...S.input, flex: 1 }}
          placeholder="new@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} />
        <button type="submit" style={S.btnPrimary} disabled={saving}>{saving ? '…' : 'Add'}</button>
      </form>
      {error && <p style={{ ...S.error, marginTop: 8 }}>{error}</p>}
    </div>
  );
}


// ── Overview Dashboard (all-clients summary) ─────────────────────────────────
function OverviewDashboard({ clients, onSelect }) {
  const [audits, setAudits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [{ data: a }, { data: t }, { data: cu }] = await Promise.all([
        supabase.from('audits').select('*').order('uploaded_at', { ascending: false }),
        supabase.from('tasks').select('*'),
        supabase.from('client_users').select('client_id, last_seen'),
      ]);
      setAudits(a || []);
      const allRows = t || [];
      setTasks(allRows.map(decodeTaskRow).filter(Boolean));
      setPendingRequests(allRows.filter(r => !r.completed_at && isAuditRequest(r)));
      setClientUsers(cu || []);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) return <p style={S.muted}><LoadingDots /></p>;

  const now = new Date();
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const auditsThisMonth = audits.filter(a => new Date(a.uploaded_at) >= thisMonth);
  const auditsLastMonth = audits.filter(a => {
    const d = new Date(a.uploaded_at);
    return d >= lastMonth && d < thisMonth;
  });

  const clientsWithAudit = new Set(auditsThisMonth.map(a => a.client_id));
  const overdueClients = clients.filter(c => !clientsWithAudit.has(c.id));

  // Latest audit per client
  const latestByClient = {};
  const prevByClient = {};
  for (const a of audits) {
    if (!latestByClient[a.client_id]) latestByClient[a.client_id] = a;
    else if (!prevByClient[a.client_id]) prevByClient[a.client_id] = a;
  }

  const clientsWithScores = clients.map(c => {
    const latest = latestByClient[c.id];
    const prev = prevByClient[c.id];
    return {
      ...c,
      score: latest?.overall_score ?? null,
      prevScore: prev?.overall_score ?? null,
      diff: latest && prev ? latest.overall_score - prev.overall_score : null,
      lastAudit: latest?.uploaded_at ?? null,
    };
  });

  const scoredClients = clientsWithScores.filter(c => c.score !== null);
  const avgScore = scoredClients.length > 0
    ? Math.round(scoredClients.reduce((s, c) => s + c.score, 0) / scoredClients.length)
    : null;

  const topPerformers = [...scoredClients].sort((a, b) => b.score - a.score).slice(0, 5);
  const mostImproved = [...scoredClients].filter(c => c.diff !== null).sort((a, b) => b.diff - a.diff).slice(0, 5);
  const needsAttention = [...scoredClients].filter(c => c.diff !== null && c.diff < 0).sort((a, b) => a.diff - b.diff).slice(0, 5);

  // Tasks summary
  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'done').length;
  const todoTasks = totalTasks - doneTasks;
  const taskPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  // Pillar breakdown across all tasks
  const pillarCounts = {};
  for (const t of tasks) {
    const p = t.pillar || 'General';
    if (!pillarCounts[p]) pillarCounts[p] = { total: 0, done: 0 };
    pillarCounts[p].total++;
    if (t.status === 'done') pillarCounts[p].done++;
  }

  // Score distribution
  const buckets = { '90–100': 0, '75–89': 0, '50–74': 0, '25–49': 0, '0–24': 0 };
  for (const c of scoredClients) {
    if (c.score >= 90) buckets['90–100']++;
    else if (c.score >= 75) buckets['75–89']++;
    else if (c.score >= 50) buckets['50–74']++;
    else if (c.score >= 25) buckets['25–49']++;
    else buckets['0–24']++;
  }
  const maxBucket = Math.max(...Object.values(buckets), 1);

  // Last seen map
  const lastSeenMap = {};
  for (const cu of clientUsers) {
    if (!lastSeenMap[cu.client_id] || (cu.last_seen && cu.last_seen > lastSeenMap[cu.client_id])) {
      lastSeenMap[cu.client_id] = cu.last_seen || null;
    }
  }
  const activeClients = clients.filter(c => {
    const ls = lastSeenMap[c.id];
    return ls && (now - new Date(ls)) < 30 * 24 * 60 * 60 * 1000;
  });

  // Recent audits
  const recentAudits = audits.slice(0, 8);

  const card = (content, style = {}) => (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, ...style }}>
      {content}
    </div>
  );

  const statLabel = { fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px 0' };
  const statValue = { fontSize: 32, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1, margin: 0 };
  const statSub = { fontSize: 13, color: 'var(--text-dim)', margin: '6px 0 0 0' };

  return (
    <div>
      <h1 style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', margin: '0 0 32px 0' }}>Dashboard</h1>

      {/* Pending Audit Requests */}
      {pendingRequests.length > 0 && (
        <div style={{ marginBottom: 24, padding: '16px 20px', background: 'color-mix(in srgb, var(--warning) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 15%, transparent)', borderRadius: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 12px 0' }}>
            Audit Requests ({pendingRequests.length})
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pendingRequests.map(r => {
              const cl = clients.find(c => c.id === r.client_id);
              return (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', flex: 1 }}>{cl?.company_name || 'Unknown'}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{formatDate(r.created_at)}</span>
                  <button onClick={() => onSelect(cl, 'audit')} style={{ background: 'none', border: '1px solid color-mix(in srgb, var(--warning) 30%, transparent)', borderRadius: 10, padding: '4px 12px', fontSize: 12, color: 'var(--warning)', cursor: 'pointer', fontFamily: FONT, fontWeight: 500 }}>
                    Upload
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 32 }}>
        {card(<>
          <p style={statLabel}>Total Clients</p>
          <p style={statValue}>{clients.length}</p>
          <p style={statSub}>{activeClients.length} active in last 30 days</p>
        </>)}
        {card(<>
          <p style={statLabel}>Avg Score</p>
          <p style={statValue}>{avgScore ?? '—'}<span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-dim)' }}>%</span></p>
          <p style={statSub}>{scoredClients.length} clients with audits</p>
        </>)}
        {card(<>
          <p style={statLabel}>Audits This Month</p>
          <p style={statValue}>{auditsThisMonth.length}</p>
          <p style={statSub}>{auditsLastMonth.length} last month · {overdueClients.length} overdue</p>
        </>)}
        {card(<>
          <p style={statLabel}>Task Completion</p>
          <p style={statValue}>{taskPct}<span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-dim)' }}>%</span></p>
          <p style={statSub}>{doneTasks} done · {todoTasks} remaining</p>
        </>)}
      </div>

      {/* Score Distribution + Task Pillar Breakdown */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        {card(<>
          <p style={statLabel}>Score Distribution</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {Object.entries(buckets).map(([range, count]) => (
              <div key={range} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 56, textAlign: 'right', flexShrink: 0 }}>{range}</span>
                <div style={{ flex: 1, height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: count > 0 ? Math.max(4, (count / maxBucket) * 100) + '%' : '0%', background: 'var(--accent)', borderRadius: 999, transition: 'width 0.3s' }} />
                </div>
                <span style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600, width: 24, flexShrink: 0 }}>{count}</span>
              </div>
            ))}
          </div>
        </>)}
        {card(<>
          <p style={statLabel}>Tasks by Pillar</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {Object.entries(pillarCounts).map(([pillar, { total, done }]) => {
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <div key={pillar} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', width: 100, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pillar.split('&')[0].trim()}</span>
                  <div style={{ flex: 1, height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: 'var(--success)', borderRadius: 999, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-dim)', width: 56, flexShrink: 0, textAlign: 'right' }}>{done}/{total}</span>
                </div>
              );
            })}
          </div>
        </>)}
      </div>

      {/* Client Rankings */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 32 }}>
        {card(<>
          <p style={statLabel}>Top Performers</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {topPerformers.map((c, i) => (
              <button key={c.id} onClick={() => onSelect(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontFamily: FONT, textAlign: 'left' }}>
                <span style={{ width: 20, fontSize: 12, fontWeight: 600, color: 'var(--text-faint)' }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{c.company_name}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--success)' }}>{c.score}%</span>
              </button>
            ))}
            {topPerformers.length === 0 && <p style={S.muted}>No audits yet</p>}
          </div>
        </>)}
        {card(<>
          <p style={statLabel}>Most Improved</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {mostImproved.map((c, i) => (
              <button key={c.id} onClick={() => onSelect(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontFamily: FONT, textAlign: 'left' }}>
                <span style={{ width: 20, fontSize: 12, fontWeight: 600, color: 'var(--text-faint)' }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{c.company_name}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: c.diff >= 0 ? 'var(--success)' : 'var(--danger)' }}>{c.diff >= 0 ? '+' : ''}{c.diff}</span>
              </button>
            ))}
            {mostImproved.length === 0 && <p style={S.muted}>Need 2+ audits per client</p>}
          </div>
        </>)}
        {card(<>
          <p style={statLabel}>Needs Attention</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {needsAttention.length > 0 ? needsAttention.map((c, i) => (
              <button key={c.id} onClick={() => onSelect(c)} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', fontFamily: FONT, textAlign: 'left' }}>
                <span style={{ width: 20, fontSize: 12, fontWeight: 600, color: 'var(--text-faint)' }}>{i + 1}</span>
                <span style={{ flex: 1, fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{c.company_name}</span>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--danger)' }}>{c.diff}</span>
              </button>
            )) : (
              <p style={{ ...S.muted, color: 'var(--success)' }}>All clients improving</p>
            )}
          </div>
        </>)}
      </div>

      {/* Recent Activity + Overdue */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 32 }}>
        {card(<>
          <p style={statLabel}>Recent Audits</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {recentAudits.map(a => {
              const cl = clients.find(c => c.id === a.client_id);
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--bg-btn)' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-dim)', width: 88, flexShrink: 0 }}>{formatDate(a.uploaded_at)}</span>
                  <span style={{ flex: 1, fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{cl?.company_name || '—'}</span>
                  <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{a.overall_score}%</span>
                </div>
              );
            })}
            {recentAudits.length === 0 && <p style={S.muted}>No audits yet</p>}
          </div>
        </>)}
        {card(<>
          <p style={statLabel}>Overdue This Month ({overdueClients.length})</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            {overdueClients.length > 0 ? overdueClients.map(c => {
              const last = latestByClient[c.id];
              return (
                <button key={c.id} onClick={() => onSelect(c, 'audit')} style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0', borderBottom: '1px solid var(--bg-btn)', fontFamily: FONT, textAlign: 'left', width: '100%' }}>
                  <span style={{ flex: 1, fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{c.company_name}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{last ? 'Last: ' + formatDate(last.uploaded_at) : 'No audits'}</span>
                </button>
              );
            }) : (
              <p style={{ ...S.muted, color: 'var(--success)' }}>All audits current</p>
            )}
          </div>
        </>)}
      </div>

      {/* All Clients Table */}
      {card(<>
        <p style={statLabel}>All Clients</p>
        <div style={{ marginTop: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8, padding: '0 0 8px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Client</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Trade</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Score</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Change</span>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Last Audit</span>
          </div>
          {clientsWithScores.map(c => (
            <button key={c.id} onClick={() => onSelect(c)} style={{
              display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: 8,
              padding: '10px 0', borderBottom: '1px solid var(--bg-btn)',
              background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT, textAlign: 'left', width: '100%',
            }}>
              <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>{c.company_name}</span>
              <span style={{ fontSize: 13, color: 'var(--text-dim)' }}>{c.trade}</span>
              <span style={{ fontSize: 14, fontWeight: 600, color: c.score != null ? 'var(--text-bright)' : 'var(--text-faint)' }}>{c.score != null ? c.score + '%' : '—'}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: c.diff == null ? 'var(--text-faint)' : c.diff >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {c.diff != null ? (c.diff >= 0 ? '+' : '') + c.diff : '—'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{c.lastAudit ? formatDate(c.lastAudit) : '—'}</span>
            </button>
          ))}
        </div>
      </>, { marginBottom: 32 })}
    </div>
  );
}


// ── Client Overview (per-client dashboard – AEO) ────────────────────────────
function ClientOverview({ client }) {
  const [audits, setAudits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const [{ data: a }, { data: t }] = await Promise.all([
        supabase.from('audits').select('*').eq('client_id', client.id).order('uploaded_at', { ascending: false }),
        supabase.from('tasks').select('*').eq('client_id', client.id),
      ]);
      setAudits(a || []);
      setTasks((t || []).map(decodeTaskRow).filter(Boolean));
      setLoading(false);
    }
    load();
  }, [client.id]);

  if (loading) return <p style={S.muted}><LoadingDots /></p>;

  const latest = audits[0] || null;
  const prev = audits[1] || null;
  const latestScore = latest ? (latest.geo_score ?? latest.overall_score ?? 0) : 0;
  const prevScore = prev ? (prev.geo_score ?? prev.overall_score ?? 0) : null;
  const scoreDiff = latest && prev ? latestScore - prevScore : null;
  const firstAudit = audits.length > 0 ? audits[audits.length - 1] : null;
  const firstScore = firstAudit ? (firstAudit.geo_score ?? firstAudit.overall_score ?? 0) : 0;
  const totalDiff = latest && firstAudit && audits.length > 1 ? latestScore - firstScore : null;

  const daysSinceAudit = latest ? Math.floor((Date.now() - new Date(latest.uploaded_at)) / (1000 * 60 * 60 * 24)) : null;

  const totalTasks = tasks.length;
  const doneTasks = tasks.filter(t => t.status === 'done').length;
  const todoTasks = totalTasks - doneTasks;
  const taskPct = totalTasks > 0 ? Math.round((doneTasks / totalTasks) * 100) : 0;

  const pillarTasks = {};
  for (const t of tasks) {
    const p = t.pillar || 'General';
    if (!pillarTasks[p]) pillarTasks[p] = { total: 0, done: 0 };
    pillarTasks[p].total++;
    if (t.status === 'done') pillarTasks[p].done++;
  }

  // Countdown timers
  const nextQuickDays = countdownDays(audits, 'quick', 7);
  const nextFullDays = countdownDays(audits, 'full', 30);

  // Findings summary
  const findingCounts = {};
  if (latest?.findings && Array.isArray(latest.findings)) {
    for (const f of latest.findings) { findingCounts[f.severity] = (findingCounts[f.severity] || 0) + 1; }
  }

  // Goal progress
  const goalScore = client.goal_score;
  const goalDate = client.goal_date;
  const goalPct = goalScore != null && firstScore != null && goalScore > firstScore
    ? Math.min(100, Math.round((latestScore - firstScore) / (goalScore - firstScore) * 100))
    : null;

  const velocity = audits.length >= 2 && totalDiff != null
    ? (totalDiff / (audits.length - 1)).toFixed(1)
    : null;

  const card = (content, style = {}) => (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 16, padding: 24, ...style }}>
      {content}
    </div>
  );
  const statLabel = { fontSize: 11, fontWeight: 600, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 8px 0' };
  const statValue = { fontSize: 32, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1, margin: 0 };
  const statSub = { fontSize: 13, color: 'var(--text-dim)', margin: '6px 0 0 0' };

  if (!latest) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '48px 0' }}>
        <p style={{ fontSize: 15, color: 'var(--text-dim)' }}>No audits uploaded yet for this client.</p>
        <p style={{ fontSize: 13, color: 'var(--text-faint)' }}>Upload an audit to see the dashboard.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Countdown timers */}
      {(nextQuickDays != null || nextFullDays != null) && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          {nextQuickDays != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 10, background: 'rgba(10,132,255,0.1)', border: '1px solid rgba(10,132,255,0.2)' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: '#0A84FF' }}>Next quick audit: {nextQuickDays}d</span>
            </div>
          )}
          {nextFullDays != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 10, background: 'color-mix(in srgb, var(--accent) 8%, transparent)', border: '1px solid color-mix(in srgb, var(--accent) 20%, transparent)' }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>Next full audit: {nextFullDays}d</span>
            </div>
          )}
          {client.website_url && (
            <span style={{ fontSize: 12, color: 'var(--text-faint)', alignSelf: 'center' }}>{client.website_url}</span>
          )}
        </div>
      )}

      {/* KPI Row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
        {card(<>
          <p style={statLabel}>AEO Score</p>
          <p style={statValue}>{latestScore}<span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-dim)' }}>%</span></p>
          {scoreDiff != null && <p style={{ ...statSub, color: scoreDiff >= 0 ? 'var(--success)' : 'var(--danger)' }}>{scoreDiff >= 0 ? '+' : ''}{scoreDiff} since last</p>}
        </>)}
        {card(<>
          <p style={statLabel}>Findings</p>
          <p style={statValue}>{Object.values(findingCounts).reduce((s, c) => s + c, 0)}</p>
          <p style={statSub}>
            {['critical', 'high', 'medium', 'low'].filter(s => findingCounts[s]).map(s => (
              <span key={s} style={{ color: SEVERITY_COLORS[s], marginRight: 8 }}>{findingCounts[s]} {s}</span>
            ))}
            {Object.keys(findingCounts).length === 0 && 'None detected'}
          </p>
        </>)}
        {card(<>
          <p style={statLabel}>Tasks</p>
          <p style={statValue}>{taskPct}<span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-dim)' }}>%</span></p>
          <p style={statSub}>{doneTasks} done · {todoTasks} todo</p>
        </>)}
        {card(<>
          <p style={statLabel}>Last Audit</p>
          <p style={statValue}>{daysSinceAudit}<span style={{ fontSize: 16, fontWeight: 400, color: 'var(--text-dim)' }}>d</span></p>
          <p style={statSub}>{daysSinceAudit <= 35 ? 'Current' : 'Overdue'} · {audits.length} total</p>
        </>)}
      </div>

      {/* AEO Category Scores + Platform Scores */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {card(<>
          <p style={statLabel}>AEO Category Breakdown</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
            {AEO_CATEGORIES.map(cat => {
              const val = latest[cat.key] ?? 0;
              return (
                <div key={cat.key}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{cat.label} <span style={{ fontSize: 11, color: 'var(--text-faint)' }}>({cat.weight})</span></span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{val}/100</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: val + '%', background: cat.color, borderRadius: 999, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </>)}
        {card(<>
          <p style={statLabel}>Platform Visibility</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {latest.platforms && typeof latest.platforms === 'object' ? (
              Object.entries(latest.platforms).map(([name, val]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 140, flexShrink: 0 }}>{name}</span>
                  <div style={{ flex: 1, height: 5, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: val + '%', background: 'var(--accent)', borderRadius: 999 }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', width: 32, textAlign: 'right' }}>{val}</span>
                </div>
              ))
            ) : (
              AEO_PLATFORMS.map(name => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', width: 140, flexShrink: 0 }}>{name}</span>
                  <div style={{ flex: 1, height: 5, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: '0%', background: 'var(--accent)', borderRadius: 999 }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-dim)', width: 32, textAlign: 'right' }}>—</span>
                </div>
              ))
            )}
          </div>
        </>)}
      </div>

      {/* Task Progress + Goal */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        {card(<>
          <p style={statLabel}>Task Progress by Pillar</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 16 }}>
            {Object.entries(pillarTasks).map(([pillar, { total, done }]) => {
              const pct = total > 0 ? Math.round((done / total) * 100) : 0;
              return (
                <div key={pillar}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pillar.split('&')[0].trim()}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{done}/{total}</span>
                  </div>
                  <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: pct + '%', background: 'var(--success)', borderRadius: 999, transition: 'width 0.3s' }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(pillarTasks).length === 0 && <p style={S.muted}>No tasks yet</p>}
          </div>
        </>)}
        {card(<>
          <p style={statLabel}>Progress & Velocity</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
            {goalPct != null && (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Goal: {goalScore}/100{goalDate ? ' by ' + formatDate(goalDate + 'T00:00:00') : ''}</span>
                  <span style={{ fontSize: 13, fontWeight: 600, color: goalPct >= 100 ? 'var(--success)' : 'var(--text)' }}>{goalPct}%</span>
                </div>
                <div style={{ height: 6, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: goalPct + '%', background: goalPct >= 100 ? 'var(--success)' : 'var(--accent)', borderRadius: 999, transition: 'width 0.3s' }} />
                </div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
              <div>
                <p style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 4px 0' }}>Velocity</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{velocity != null ? (velocity > 0 ? '+' : '') + velocity : '—'}</p>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '2px 0 0 0' }}>pts/audit</p>
              </div>
              <div>
                <p style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 4px 0' }}>Total Change</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: totalDiff != null ? (totalDiff >= 0 ? 'var(--success)' : 'var(--danger)') : 'var(--text-dim)', margin: 0 }}>{totalDiff != null ? (totalDiff >= 0 ? '+' : '') + totalDiff : '—'}</p>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '2px 0 0 0' }}>all time</p>
              </div>
              <div>
                <p style={{ fontSize: 11, color: 'var(--text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 4px 0' }}>Audits</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0 }}>{audits.length}</p>
                <p style={{ fontSize: 11, color: 'var(--text-dim)', margin: '2px 0 0 0' }}>total</p>
              </div>
            </div>
          </div>
        </>)}
      </div>

      {/* Score History */}
      {audits.length >= 2 && card(<>
        <p style={statLabel}>AEO Score History</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
          {[...audits].reverse().map((a, i, arr) => {
            const s = a.geo_score ?? a.overall_score ?? 0;
            const ps = i > 0 ? (arr[i - 1].geo_score ?? arr[i - 1].overall_score ?? 0) : null;
            const diff = ps != null ? s - ps : null;
            const typeLabel = a.audit_type === 'quick' ? 'Q' : 'F';
            return (
              <div key={a.id} style={{ display: 'grid', gridTemplateColumns: '88px 24px 1fr 48px 48px', gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--bg-btn)' }}>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{formatDate(a.uploaded_at)}</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: a.audit_type === 'quick' ? '#0A84FF' : 'var(--accent)', textAlign: 'center' }}>{typeLabel}</span>
                <div style={{ height: 4, background: 'var(--bg-input)', borderRadius: 999, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: s + '%', background: 'var(--accent)', borderRadius: 999 }} />
                </div>
                <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', textAlign: 'right' }}>{s}%</span>
                <span style={{ fontSize: 12, fontWeight: 600, textAlign: 'right', color: diff == null ? 'var(--text-dim)' : diff >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                  {diff != null ? (diff >= 0 ? '+' : '') + diff : '—'}
                </span>
              </div>
            );
          })}
        </div>
      </>, { marginBottom: 24 })}
    </div>
  );
}

// ── Client Editor ────────────────────────────────────────────────────────────
function ClientEditor({ client, initialTab = 'overview', onDelete, deleting, hasPendingRequest, onDismissRequest, onTabChange }) {
  const [tab, setTabRaw] = useState(initialTab);
  const setTab = (t) => { setTabRaw(t); if (onTabChange) onTabChange(t); };
  const [clientData, setClientData] = useState(client);
  const [requestDismissed, setRequestDismissed] = useState(false);
  const [editingUrl, setEditingUrl] = useState(false);
  const [urlDraft, setUrlDraft] = useState('');
  const [urlSaving, setUrlSaving] = useState(false);

  useEffect(() => { setClientData(client); setTabRaw(initialTab); setRequestDismissed(false); setEditingUrl(false); }, [client, initialTab]);

  async function saveWebsiteUrl() {
    const trimmed = urlDraft.trim();
    setUrlSaving(true);
    const { error } = await supabase.from('clients').update({ website_url: trimmed || null }).eq('id', clientData.id);
    setUrlSaving(false);
    if (error) return;
    setClientData(d => ({ ...d, website_url: trimmed || null }));
    setEditingUrl(false);
  }

  const TABS = [
    { key: 'overview', label: 'Overview' },
    { key: 'tasks',   label: 'Tasks' },
    { key: 'audit',   label: 'AEO Audit' },
    { key: 'goal',    label: 'Goal' },
    { key: 'entity',  label: 'Business profile' },
    { key: 'notes',   label: 'Notes' },
    { key: 'preview', label: 'Portal Preview' },
  ];

  return (
    <div>
      {/* Header */}
      <div style={S.clientHeader}>
        <div>
          <h2 style={S.clientName}>{clientData.company_name}</h2>
          <span style={S.clientMeta}>
            {clientData.trade} · {clientData.county}
            {editingUrl ? (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 8 }}>
                <input
                  autoFocus
                  type="url"
                  placeholder="https://example.com"
                  value={urlDraft}
                  onChange={e => setUrlDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveWebsiteUrl(); if (e.key === 'Escape') setEditingUrl(false); }}
                  style={{ ...S.input, width: 220, padding: '4px 8px', fontSize: 12, display: 'inline-block' }}
                />
                <button onClick={saveWebsiteUrl} disabled={urlSaving}
                  style={{ ...S.btnPrimary, fontSize: 11, padding: '4px 10px' }}>
                  {urlSaving ? '…' : 'Save'}
                </button>
                <button onClick={() => setEditingUrl(false)}
                  style={{ ...S.btnSecondary, fontSize: 11, padding: '4px 10px' }}>
                  Cancel
                </button>
              </span>
            ) : clientData.website_url ? (
              <>
                {' · '}
                <a href={clientData.website_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>{clientData.website_url.replace(/^https?:\/\//, '')}</a>
                <button onClick={() => { setUrlDraft(clientData.website_url); setEditingUrl(true); }}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-faint)', fontSize: 11, padding: '0 4px', verticalAlign: 'middle' }}
                  title="Edit website URL">✎</button>
              </>
            ) : (
              <button onClick={() => { setUrlDraft(''); setEditingUrl(true); }}
                style={{ marginLeft: 8, background: 'none', border: '1px dashed var(--border-strong)', borderRadius: 6, cursor: 'pointer', color: 'var(--accent)', fontSize: 11, padding: '3px 10px', fontFamily: S.clientMeta?.fontFamily || 'inherit' }}>
                + Add website URL
              </button>
            )}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {onDelete && (
            <button style={S.btnDanger} disabled={deleting}
              onClick={() => { if (window.confirm(`Delete "${clientData.company_name}"? This cannot be undone.`)) onDelete(clientData); }}>
              {deleting ? '…' : 'Delete'}
            </button>
          )}
        </div>
      </div>

      {/* Audit Request Banner */}
      {hasPendingRequest && !requestDismissed && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'color-mix(in srgb, var(--warning) 6%, transparent)', border: '1px solid color-mix(in srgb, var(--warning) 15%, transparent)', borderRadius: 12, marginBottom: 16 }}>
          <span style={{ fontSize: 15, flexShrink: 0 }}>📋</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--warning)', margin: 0 }}>Audit Requested</p>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '2px 0 0 0' }}>This client has requested a new audit.</p>
          </div>
          <button onClick={() => { setRequestDismissed(true); if (onDismissRequest) onDismissRequest(clientData.id); }}
            style={{ ...S.btnSecondary, fontSize: 12, padding: '6px 12px' }}>Dismiss</button>
          <button onClick={() => { setTab('audit'); setRequestDismissed(true); if (onDismissRequest) onDismissRequest(clientData.id); }}
            style={{ ...S.btnPrimary, fontSize: 12, padding: '6px 12px' }}>Upload Audit</button>
        </div>
      )}

      {/* Tabs */}
      <div style={S.tabs}>
        {TABS.map(t => (
          <button key={t.key}
            style={{ ...S.tab, ...(tab === t.key ? S.tabActive : {}) }}
            onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={tab === 'preview' ? {} : tab === 'audit' ? { maxWidth: 720 } : tab === 'entity' ? { maxWidth: 720 } : {}}>
        {tab === 'overview' && <ClientOverview client={clientData} />}
        {tab === 'tasks'   && <TasksTab client={clientData} />}
        {tab === 'audit'   && <AuditForm client={clientData} />}
        {tab === 'goal'    && <GoalTab client={clientData} onSaved={updates => setClientData(d => ({ ...d, ...updates }))} />}
        {tab === 'entity'  && (
          <EntityProfileForm
            clientId={clientData.id}
            onSaved={updates => setClientData(d => ({ ...d, ...updates }))}
          />
        )}
        {tab === 'notes'   && <NotesTab client={clientData} />}
        {tab === 'preview' && (
          <div style={S.previewWrap}>
            <Dashboard clientId={clientData.id} preview />
          </div>
        )}
      </div>

      {/* Collapsible Sections */}
      {tab !== 'preview' && tab !== 'entity' && (
        <>
          <CollapsibleSection title="Audit History">
            <AuditHistorySection client={clientData} />
          </CollapsibleSection>
          <CollapsibleSection title="Authorized Emails" defaultOpen={false}>
            <AuthorizedEmailsSection client={clientData} />
          </CollapsibleSection>
        </>
      )}
    </div>
  );
}

// ── Admin Root ───────────────────────────────────────────────────────────────
function readHash() {
  const h = window.location.hash.replace('#', '');
  const params = new URLSearchParams(h);
  return { clientId: params.get('client'), tab: params.get('tab') || 'overview' };
}

function writeHash(clientId, tab) {
  if (clientId) {
    window.location.hash = `client=${clientId}&tab=${tab || 'overview'}`;
  } else {
    if (window.location.hash) window.history.replaceState(null, '', window.location.pathname);
  }
}

export default function Admin({ onSignOut, isDark, onToggleTheme }) {
  const [clients, setClients] = useState([]);
  const [recentAuditIds, setRecentAuditIds] = useState(new Set());
  const [selected, setSelected] = useState(null);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [showNew, setShowNew] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showResources, setShowResources] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [adminEmail, setAdminEmail] = useState(null);
  const restoredRef = useRef(false);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => { if (data?.user?.email) setAdminEmail(data.user.email); });
  }, []);

  const [requestClientIds, setRequestClientIds] = useState(new Set());
  const [pendingAudits, setPendingAudits] = useState([]);

  const loadClients = useCallback(async () => {
    setLoading(true);
    const cutoff = new Date(Date.now() - 35 * 24 * 60 * 60 * 1000).toISOString();
    const [{ data: clientData }, { data: auditData }, { data: allTasks }, { data: pending }] = await Promise.all([
      supabase.from('clients').select('*').order('company_name'),
      supabase.from('audits').select('client_id').gte('uploaded_at', cutoff),
      supabase.from('tasks').select('client_id, description, completed_at'),
      supabase.from('audits').select('*').eq('published', false).order('uploaded_at', { ascending: false }),
    ]);
    setClients(clientData || []);
    setRecentAuditIds(new Set((auditData || []).map(a => a.client_id)));
    setPendingAudits(pending || []);
    const reqIds = new Set();
    for (const row of (allTasks || [])) {
      if (!row.completed_at && isAuditRequest(row)) reqIds.add(row.client_id);
    }
    setRequestClientIds(reqIds);
    setLoading(false);
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  useEffect(() => {
    if (restoredRef.current || clients.length === 0) return;
    restoredRef.current = true;
    const { clientId, tab } = readHash();
    if (clientId) {
      const match = clients.find(c => c.id === clientId);
      if (match) { setSelected(match); setSelectedTab(tab); }
    }
  }, [clients]);

  async function publishAudit(auditId) {
    await supabase.from('audits').update({ published: true }).eq('id', auditId);
    setPendingAudits(prev => prev.filter(a => a.id !== auditId));
  }

  async function deletePendingAudit(auditId) {
    await supabase.from('audits').delete().eq('id', auditId);
    setPendingAudits(prev => prev.filter(a => a.id !== auditId));
  }

  function selectClient(c, tab = 'overview') {
    setSelected(c); setSelectedTab(tab); setShowNew(false); setShowSettings(false); setShowResources(false);
    writeHash(c?.id, tab);
  }

  async function deleteClient(client) {
    setDeletingId(client.id);
    await supabase.from('tasks').delete().eq('client_id', client.id);
    await supabase.from('audits').delete().eq('client_id', client.id);
    await supabase.from('client_users').delete().eq('client_id', client.id);
    const { error } = await supabase.from('clients').delete().eq('id', client.id);
    setDeletingId(null);
    if (error) { alert(error.message); return; }
    if (selected?.id === client.id) { setSelected(null); writeHash(null); }
    loadClients();
  }

  return (
    <div style={S.adminPage}>
      <header style={S.header}>
        <span style={S.headerBrand}>Iris Digital Consulting — Admin</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <ThemeToggle isDark={isDark} onToggle={onToggleTheme} />
          <button style={S.refreshBtn} title="Refresh" onClick={async () => {
            setRefreshing(true); await loadClients(); setRefreshing(false);
          }}>
            <svg className={refreshing ? 'spin' : ''} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
          <button style={{ ...S.signOut, ...(showResources ? { color: 'var(--accent)', borderColor: 'color-mix(in srgb, var(--accent) 30%, transparent)' } : {}) }}
            onClick={() => { setShowResources(s => !s); setSelected(null); writeHash(null); setShowNew(false); setShowSettings(false); }}>
            {showResources ? 'Close Resources' : 'Resources'}
          </button>
          <button style={S.signOut} onClick={() => { setShowSettings(s => !s); setSelected(null); writeHash(null); setShowNew(false); setShowResources(false); }}>
            {showSettings ? 'Close Settings' : 'Settings'}
          </button>
          <button style={S.signOut} onClick={onSignOut}>Sign out</button>
        </div>
      </header>

      {!loading && (
        <OverdueBanner clients={clients} recentAuditIds={recentAuditIds} onSelect={c => selectClient(c, 'audit')} />
      )}

      {pendingAudits.length > 0 && (
        <PendingAuditsBanner
          audits={pendingAudits}
          clients={clients}
          onPublish={publishAudit}
          onDelete={deletePendingAudit}
          onEdit={(audit) => {
            const cl = clients.find(c => c.id === audit.client_id);
            if (cl) selectClient(cl, 'audit');
          }}
        />
      )}

      <div style={S.columns}>
        {/* Left panel */}
        <div style={S.leftPanel}>
          <h2 style={S.panelHead}>Clients</h2>
          <button style={S.addClientBtn} title="New Client"
            onClick={() => { setShowNew(true); setSelected(null); writeHash(null); setShowSettings(false); }}>
            +
          </button>
          {loading ? (
            <p style={S.muted}><LoadingDots /></p>
          ) : (
            <div style={S.clientGrid}>
              {clients.map((c, ci) => {
                const isActive = selected?.id === c.id;
                const initial = (c.company_name || '?')[0].toUpperCase();
                const hasRequest = requestClientIds.has(c.id);
                const hue = ((c.company_name || '').split('').reduce((h, ch) => h + ch.charCodeAt(0), 0) * 37) % 360;
                const bg = isActive ? `hsla(${hue}, 60%, 50%, 0.2)` : `hsla(${hue}, 50%, 45%, 0.12)`;
                const clr = isActive ? 'var(--text-bright)' : `hsl(${hue}, 55%, ${isDark ? '68%' : '38%'})`;
                const bdr = isActive ? `hsl(${hue}, 60%, 50%)` : 'transparent';
                return (
                  <button key={c.id} title={c.company_name}
                    style={{ ...S.avatarBtn, background: bg, color: clr, borderColor: bdr, position: 'relative' }}
                    onClick={() => selectClient(c)}>
                    {initial}
                    {hasRequest && (
                      <span style={{ position: 'absolute', top: -2, right: -2, width: 10, height: 10, borderRadius: 5, background: 'var(--warning)', border: '2px solid var(--bg-surface)' }} />
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Right panel */}
        <div style={S.rightPanel}>
          {showResources && !showSettings && (
            <Resources user={{ email: adminEmail }} role="editor" onSignOut={null} embedded />
          )}
          {showSettings && !showResources && <AdminSettings />}
          {showNew && !showSettings && !showResources && (
            <NewClientForm
              onCreated={() => { setShowNew(false); loadClients(); }}
              onCancel={() => setShowNew(false)} />
          )}
          {!showNew && !showSettings && !showResources && selected && (
            <ClientEditor key={selected.id} client={selected} initialTab={selectedTab}
              onDelete={deleteClient} deleting={deletingId === selected.id}
              hasPendingRequest={requestClientIds.has(selected.id)}
              onTabChange={(t) => { setSelectedTab(t); writeHash(selected.id, t); }}
              onDismissRequest={async (cid) => {
                const { data: rows } = await supabase.from('tasks').select('id, description, completed_at').eq('client_id', cid);
                for (const r of (rows || [])) {
                  if (!r.completed_at && isAuditRequest(r)) {
                    await supabase.from('tasks').update({ completed_at: new Date().toISOString() }).eq('id', r.id);
                  }
                }
                setRequestClientIds(prev => { const next = new Set(prev); next.delete(cid); return next; });
              }} />
          )}
          {!showNew && !showSettings && !showResources && !selected && (
            <OverviewDashboard clients={clients} onSelect={selectClient} />
          )}
        </div>
      </div>
      {selected && !showNew && !showSettings && (
        <Messenger clientId={selected.id} role="admin" senderEmail={adminEmail} dark={isDark} />
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  adminPage: { fontFamily: FONT, minHeight: '100vh', background: 'var(--bg)' },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '0 24px', height: 48,
    background: 'var(--header-bg)', backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    borderBottom: '1px solid var(--border-subtle)',
    position: 'sticky', top: 0, zIndex: 100,
  },
  headerBrand: { fontWeight: 600, fontSize: 15, color: 'var(--text)', letterSpacing: '-0.01em' },
  signOut: {
    background: 'none', border: '1px solid var(--border-strong)', borderRadius: 980,
    padding: '5px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 400,
    fontFamily: FONT, color: 'var(--text-muted)', transition: 'all 0.15s ease',
  },
  refreshBtn: {
    width: 32, height: 32, borderRadius: 980, background: 'none',
    border: '1px solid var(--border-strong)', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: 'var(--text-muted)', padding: 0, flexShrink: 0, transition: 'all 0.15s ease',
  },
  columns: { display: 'flex', gap: 0, minHeight: 'calc(100vh - 48px)' },
  leftPanel: {
    width: 72, minWidth: 72, background: 'var(--bg-surface)',
    borderRight: '1px solid var(--border-subtle)', padding: '16px 8px',
    display: 'flex', flexDirection: 'column', alignItems: 'center',
  },
  rightPanel: { flex: 1, padding: '36px 48px', overflowY: 'auto', background: 'var(--bg)' },
  panelHead: {
    fontSize: 11, fontWeight: 600, margin: '0 0 16px 0', color: 'var(--text-dim)',
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  addClientBtn: {
    width: 48, height: 40, borderRadius: 10, border: '1px dashed var(--border-dashed)',
    background: 'none', color: 'var(--text-dim)', fontSize: 20, fontWeight: 400,
    cursor: 'pointer', fontFamily: FONT, display: 'flex', alignItems: 'center',
    justifyContent: 'center', marginBottom: 8, flexShrink: 0,
    transition: 'all 0.15s ease',
  },
  clientGrid: {
    display: 'flex', flexDirection: 'column', gap: 6, flex: 1,
    overflowY: 'auto', alignItems: 'center', padding: '4px 0',
  },
  avatarBtn: {
    width: 48, height: 48, borderRadius: 12, border: '2px solid transparent',
    background: 'var(--bg-input)', color: 'var(--text-muted)',
    fontSize: 18, fontWeight: 600, fontFamily: FONT,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.2s cubic-bezier(0.25,0.46,0.45,0.94)',
    flexShrink: 0,
  },
  avatarBtnActive: {
    background: 'color-mix(in srgb, var(--accent) 15%, transparent)', borderColor: 'var(--accent)', color: 'var(--text)',
  },
  clientHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 },
  clientName: { fontSize: 36, fontWeight: 700, margin: '0 0 6px 0', color: 'var(--text)', letterSpacing: '-0.03em', lineHeight: 1.15 },
  clientMeta: { fontSize: 15, color: 'var(--text-dim)', fontWeight: 400 },
  tabs: {
    display: 'flex', gap: 4, padding: 4,
    background: 'var(--bg-btn)', borderRadius: 12,
    marginBottom: 24, width: 'fit-content',
  },
  tab: {
    padding: '8px 18px', background: 'none', border: 'none',
    borderRadius: 8, cursor: 'pointer',
    fontSize: 13, fontWeight: 500, fontFamily: FONT, color: 'var(--text-dim)',
    transition: 'all 0.2s cubic-bezier(0.25,0.46,0.45,0.94)',
  },
  tabActive: { background: 'var(--border)', color: 'var(--text)' },
  
  previewWrap: { margin: '-40px -48px', borderRadius: 16, overflow: 'hidden', background: 'var(--bg)' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  sectionHead: { fontSize: 13, fontWeight: 600, color: 'var(--text-dim)', margin: '8px 0 0 0', letterSpacing: '-0.01em' },
  label: { fontSize: 12, fontWeight: 500, color: 'var(--text-dim)', letterSpacing: '0.02em' },
  input: {
    padding: '10px 14px', border: '1px solid var(--border-medium)', borderRadius: 10,
    fontSize: 15, fontWeight: 400, fontFamily: FONT, outline: 'none', width: '100%',
    boxSizing: 'border-box', background: 'var(--bg-input)', color: 'var(--text)',
    transition: 'border-color 0.2s ease',
  },
  checkLabel: { fontSize: 14, display: 'flex', alignItems: 'center', cursor: 'pointer', color: 'var(--text-muted)' },
  btnPrimary: {
    padding: '10px 24px', background: 'var(--accent)', color: '#fff', border: 'none',
    borderRadius: 12, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: FONT,
    transition: 'all 0.2s cubic-bezier(0.25,0.46,0.45,0.94)',
  },
  btnSecondary: {
    padding: '8px 16px', background: 'transparent', color: 'var(--text-muted)',
    border: '1px solid var(--border-strong)', borderRadius: 12,
    fontSize: 13, fontWeight: 400, cursor: 'pointer', fontFamily: FONT,
    whiteSpace: 'nowrap', transition: 'all 0.15s ease',
  },
  btnDanger: {
    padding: '8px 16px', background: 'transparent', color: 'var(--danger)',
    border: '1px solid var(--danger)', borderRadius: 12,
    fontSize: 13, fontWeight: 400, cursor: 'pointer', fontFamily: FONT,
    transition: 'all 0.15s ease', borderColor: 'color-mix(in srgb, var(--danger) 25%, transparent)',
  },
  historyRow: {
    display: 'flex', alignItems: 'center',
    background: 'var(--bg-card)', border: '1px solid var(--border-subtle)',
    borderRadius: 12, padding: '12px 16px', transition: 'background 0.15s ease',
  },
  taskRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 16px', border: '1px solid var(--border-subtle)',
    borderRadius: 12, background: 'var(--bg-card)',
  },
  dialogOverlay: {
    position: 'fixed', inset: 0, background: 'var(--overlay)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  dialog: {
    background: 'var(--bg-elevated)', border: '1px solid var(--border-medium)',
    borderRadius: 20, padding: 36, maxWidth: 520, width: '92vw',
    boxShadow: '0 8px 32px var(--shadow), 0 2px 8px var(--shadow-light)',
  },
  pdfIframe: {
    width: '100%', height: 640, border: '1px solid var(--border)',
    borderRadius: 12, display: 'block', background: 'var(--bg-surface)',
  },
  parsingBanner: {
    display: 'flex', alignItems: 'center', padding: '10px 16px',
    background: 'var(--bg-btn)', border: '1px solid var(--border-subtle)',
    borderRadius: 12,
  },
  parseHint: { fontSize: 13, color: 'var(--text-dim)', margin: '4px 0 0 0' },
  parseErrorMsg: {
    fontSize: 14, color: 'var(--warning)', margin: 0, background: 'color-mix(in srgb, var(--warning) 8%, transparent)',
    border: '1px solid color-mix(in srgb, var(--warning) 15%, transparent)', borderRadius: 12, padding: '10px 14px',
  },
  error: { fontSize: 14, color: 'var(--danger)', margin: 0 },
  successMsg: { fontSize: 14, color: 'var(--success)', margin: 0 },
  muted: { fontSize: 14, color: 'var(--text-dim)', margin: 0 },
  savedMsg: { fontSize: 13, color: 'var(--success)', fontWeight: 500 },
  overdueBanner: {
    background: 'color-mix(in srgb, var(--danger) 6%, transparent)',
    borderBottom: '1px solid color-mix(in srgb, var(--danger) 10%, transparent)',
    borderLeft: '3px solid var(--danger)', padding: '10px 24px', fontSize: 13,
    color: 'var(--danger)',
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 4,
  },
};
